import { useCallback, useEffect, useRef, useState } from "react";

import { CommonKVNode } from "../../../types";
import { KVNodeEdit } from "./kvStoreSeed";

// At most one node edits at a time, identified by its full delimiter-joined
// key. Its own key segment and its value open together in one editor.
export type NodeEditing = {
  key: string;
  segment: string;
  originalSegment: string;
  text: string;
  originalText: string;
  isInvalid?: boolean;
  isString: boolean;
  // Which field takes focus when the editor opens: the column that was
  // clicked.
  focus: "key" | "value";
};

// The node editor's lifecycle: open on one node, track its segment and
// text, apply on a click outside or a Tab out, close.
const useNodeEditing = (editNode: (key: string, edit: KVNodeEdit) => void) => {
  const [editing, setEditing] = useState<NodeEditing | null>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLTextAreaElement>(null);

  const startEditing = (
    key: string,
    segment: string,
    node: CommonKVNode,
    focus: "key" | "value"
  ) => {
    // A structural node's value starts empty — the edit adds its first
    // value, typed as plain text like any new value.
    const hasValue = node.value !== undefined && node.value !== null;
    const isString = !hasValue || typeof node.value === "string";
    const text = !hasValue
      ? ""
      : isString
        ? (node.value as string)
        : JSON.stringify(node.value);
    setEditing({
      key,
      segment,
      focus,
      originalSegment: segment,
      text,
      originalText: text,
      isString,
    });
  };

  // Applies whatever changed in the open editor — the segment rename and
  // the value edit land together as one apply.
  const applyEditing = useCallback(() => {
    if (!editing) {
      return;
    }
    const edit: KVNodeEdit = {};
    const segment = editing.segment.trim();
    if (segment && segment !== editing.originalSegment) {
      edit.segment = segment;
    }
    if (editing.text !== editing.originalText) {
      if (editing.isString) {
        edit.value = editing.text;
      } else {
        try {
          edit.value = JSON.parse(editing.text);
        } catch {
          setEditing({ ...editing, isInvalid: true });
          return;
        }
      }
    }
    setEditing(null);
    if ("segment" in edit || "value" in edit) {
      editNode(editing.key, edit);
    }
  }, [editing, editNode]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    // Clicking anywhere outside the pair of inputs applies the edit.
    const handleMouseDown = (event: MouseEvent) => {
      if (
        keyInputRef.current?.contains(event.target as Node) ||
        valueInputRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      applyEditing();
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [editing, applyEditing]);

  return {
    editing,
    keyInputRef,
    valueInputRef,
    startEditing,
    // Typing clears the invalid flag; the next apply re-checks.
    changeEditing: (patch: Partial<Pick<NodeEditing, "segment" | "text">>) =>
      setEditing(
        (current) => current && { ...current, ...patch, isInvalid: undefined }
      ),
    applyEditing,
  };
};

export type NodeEditor = ReturnType<typeof useNodeEditing>;

export default useNodeEditing;
